// ABOUTME: Renders the staged bulk-action workflow for metadata-driven lists.
// ABOUTME: Validates selection limits before loading candidates into the browser.
import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import useList from "../..";
import { useAuthState } from "_ReactContext/AuthContext";
import { useGlobalDialog } from "_ReactContext/GlobalDialogContext";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import {
    BulkActionsType,
    getBulkActionsList,
    IListBulk,
    stepValidations,
    useBulkLoadList,
    IBulkActions,
    getBulkChangeStageActionName
} from "./helperBulkActions";
import BulkActionsStep1 from "./BulkActionsStep1";
import BulkActionsStep2 from "./BulkActionsStep2";
import BulkActionsStep3AddToJob from "./BulkActionsStep3AddToJob";
import BulkActionsStep3ApplicantAddTags from "./BulkActionsStep3ApplicantAddTags";
import BulkActionsStep3AddToStageOrDisqualify from "./BulkActionsStep3ChangeStage";
import BulkActionsStep3SendEmail from "./BulkActionsStep3SendEmail";
import BulkActionsStep3SendWhatsappTemplate from "./BulkActionsStep3SendWhatsappTemplate";
import BulkActionsStep3WhatsappManualOptIn from "./BulkActionsStep3WhatsappManualOptIn";
import BulkActionsStep3RequestEvaluation from "./BulkActionsStep3RequestEvaluation";
import BulkActionsStep3DeleteApplicant from "./BulkActionsStep3DeleteApplicant";
import BulkActionsStep3SendAssessment from "./BulkActionsStep3SendAssessment";
import BulkActionsStep3JobAIMatch from "./BulkActionsStep3JobAIMatch";
import { BulkActionsStep3ApplicantExport } from "./BulkActionsStep3ApplicantExport/index";
import { BulkActionsStep3ImportApplicants } from "./BulkActionsStep3ImportApplicants";
import BulkActionsStepConfirmation from "./BulkActionsStepConfirmation";
import BulkActionsList from "./BulkActionsList";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import BulkActionsStep3JobExport from "./BulkActionsStep3JobExport";
import RefreshIcon from "@material-ui/icons/Refresh";
import {
    shouldLoadBulkList,
    shouldLoadBulkTotal,
    shouldRenderBulkActionsModal
} from "./bulkListPolicy";
import { useBulkListTotal } from "./useBulkListTotal";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        instructions: {
            marginBottom: theme.spacing(3)
        }
    })
);

interface IPropsItemFilter {
    open: boolean;
    onClose: (value?: string) => void;
    otherGenericParamsListQuery?: Compleo.IObject;
    BulkActionsRemoveFromList?: BulkActionsType[];
    fromJobViewJobId?: string;
}

const BulkActionsModalContent = (props: IPropsItemFilter) => {
    const {
        onClose,
        open,
        otherGenericParamsListQuery = {},
        BulkActionsRemoveFromList = [],
        fromJobViewJobId
    } = props;
    const bulkListQueryParams = {
        ...otherGenericParamsListQuery,
        ...(fromJobViewJobId ? { jobId: fromJobViewJobId } : {})
    };
    const formikRef = React.useRef(null);
    const classes = useStyles();
    const [emailSelectionStale, setEmailSelectionStale] = React.useState(false);
    const [loadingButtons, setLoadingButtons] = React.useState(false);
    const [stepBulk, setStepBulk] = React.useState<number>(1);
    const [listBulk, setListBulk] = React.useState<IListBulk[]>([]);
    const [loadedBulkListScope, setLoadedBulkListScope] = React.useState("");
    const [listData] = useList();
    const { company } = useAuthState();
    const [submitCalled, setSubmitCalled] = React.useState(false);
    const resetBulkSelection = React.useCallback(() => {
        setLoadingButtons(false);
        setListBulk([]);
        setLoadedBulkListScope("");
        setStepBulk(1);
    }, []);

    React.useEffect(() => {
        // post está dentro do effect por causa dos botões do modal
        // para ficarem como loading... (Necessário rerender para atualizar)

        if (submitCalled) {
            const formikValue: any = formikRef.current;
            const finishSubmission = () => setLoadingButtons(false);
            if (typeof formikValue?.submitForm === "function") {
                try {
                    const submission = formikValue.submitForm();
                    Promise.resolve(submission).then(
                        finishSubmission,
                        finishSubmission
                    );
                } catch {
                    finishSubmission();
                }
            } else {
                try {
                    formikValue?.dispatchEvent(
                        new Event("submit", {
                            cancelable: true,
                            bubbles: true
                        })
                    );
                } finally {
                    finishSubmission();
                }
            }
            setSubmitCalled(false);
        }
    }, [submitCalled]);

    const { t } = listData.tempInfo;
    const bulkActionsList = getBulkActionsList(
        listData.tempInfo.moduleName,
        "",
        BulkActionsRemoveFromList
    );
    const [bulkType, setBulkType] = React.useState<IBulkActions>(
        bulkActionsList[0]
    );

    const { callDialog } = useGlobalDialog();
    const showMessage = useShowMessage();
    const loadBulkTotal = open && shouldLoadBulkTotal(bulkType?.name);
    const bulkListTotal = useBulkListTotal(
        loadBulkTotal,
        bulkType?.name,
        bulkListQueryParams
    );
    const bulkListScope = JSON.stringify({
        companyId: company.companyId,
        action: bulkType?.name,
        filters: listData.filtersInfo.filters,
        otherGenericParams: bulkListQueryParams
    });
    const hasStaleBulkList =
        loadedBulkListScope !== "" && loadedBulkListScope !== bulkListScope;
    const needsBulkList =
        open &&
        shouldLoadBulkList(stepBulk, bulkType?.name) &&
        loadedBulkListScope !== bulkListScope;
    const loadBulkList =
        needsBulkList && (!loadBulkTotal || bulkListTotal.ready);

    React.useEffect(() => {
        if (hasStaleBulkList) {
            resetBulkSelection();
        } else if (stepBulk > 1 && bulkListTotal.error) {
            setStepBulk(1);
        }
    }, [
        hasStaleBulkList,
        stepBulk,
        bulkListTotal.error,
        resetBulkSelection
    ]);

    const nextStep = async () => {
        if (hasStaleBulkList || (stepBulk === 2 && needsBulkList)) {
            return;
        }
        if (stepBulk === 1 && bulkListTotal.error) {
            bulkListTotal.retry();
            return;
        }

        switch (stepBulk) {
            case 1:
            case 2:
                {
                    const bulkActionsSettings =
                        listData.definitionInfo?.elasticDefData?.data
                            ?.otherSettings?.bulkActionsSettings || {};

                    const validationStatus = stepValidations(
                        callDialog,
                        t,
                        stepBulk,
                        bulkType,
                        bulkActionsSettings,
                        bulkListTotal.totalFiltered || {},
                        listBulk
                    );
                    if (
                        stepBulk === 1 &&
                        bulkType.name === "applicant_importFromFile"
                    ) {
                        if (validationStatus) {
                            setStepBulk(3);
                        }
                    } else {
                        if (validationStatus) {
                            setStepBulk(stepBulk + 1);
                        }
                    }
                }
                break;
            case 3:
                setLoadingButtons(true);
                setSubmitCalled(true);

                break;
            default:
                break;
        }
    };
    const previousStep = () => {
        setStepBulk(stepBulk - 1);
    };
    const fullScreenBulkTypes: BulkActionsType[] = ["applicant_importFromFile"];
    const isFullScreen =
        fullScreenBulkTypes.includes(bulkType?.name || "") &&
        stepBulk > 2 &&
        stepBulk !== 99;
    const mainContent = (
        <MainContent
            stepBulk={stepBulk}
            bulkType={bulkType}
            setBulkType={setBulkType}
            listBulk={listBulk}
            setListBulk={setListBulk}
            openDialog={open}
            formikRef={formikRef}
            setStepBulk={setStepBulk}
            otherGenericParamsListQuery={otherGenericParamsListQuery}
            BulkActionsRemoveFromList={BulkActionsRemoveFromList}
            setLoadingButtons={setLoadingButtons}
            fromJobViewJobId={fromJobViewJobId}
            nextStep={nextStep}
            previousStep={previousStep}
            onClose={onClose}
            onBulkSelectionStale={() => {
                resetBulkSelection();
                setLoadingButtons(false);
                if (bulkType?.name === "applicant_sendEmail") setEmailSelectionStale(true);
            }}
        />
    );

    return (
        <Dialog
            fullScreen={isFullScreen}
            fullWidth
            disableBackdropClick
            disableEscapeKeyDown
            maxWidth="lg"
            aria-labelledby={t("ELASTICLISTSETTINGS:ModalBulkActions_Title")}
            open={open}
        >
            <DialogTitle>
                {`${t("ELASTICLISTSETTINGS:ModalBulkActions_Title")} - ${t(
                    `ELASTICLISTSETTINGS:BulkActions_${bulkType?.name}`
                )}`}
            </DialogTitle>
            <DialogContent dividers>
                {emailSelectionStale && <Typography role="alert">{t("ELASTICLISTSETTINGS:BulkEmailSelectionStale")}</Typography>}
                {stepBulk === 99 && bulkType?.name === "applicant_sendEmail" && <Typography>{t("ELASTICLISTSETTINGS:BulkEmailQueued")}</Typography>}
                {needsBulkList ? (
                    loadBulkList ? (
                        <BulkListLoader
                            key={bulkListScope}
                            listBulk={listBulk}
                            setListBulk={setListBulk}
                            bulkActionType={bulkType.name}
                            onReady={() => {
                                setLoadedBulkListScope(bulkListScope);
                                setEmailSelectionStale(false);
                            }}
                            onError={() => {
                                setLoadedBulkListScope("");
                                setStepBulk(1);
                                bulkListTotal.retry();
                            }}
                            otherGenericParamsListQuery={bulkListQueryParams}
                        >
                            {mainContent}
                        </BulkListLoader>
                    ) : (
                        <Loading />
                    )
                ) : (
                    mainContent
                )}
            </DialogContent>
            <DialogActionsModal
                nextStep={nextStep}
                onClose={onClose}
                previousStep={previousStep}
                bulkType={bulkType}
                stepBulk={stepBulk}
                t={t}
                loadingButtons={loadingButtons}
                nextDisabled={
                    hasStaleBulkList ||
                    (stepBulk === 2 && needsBulkList) ||
                    (stepBulk === 1 &&
                        loadBulkTotal &&
                        !bulkListTotal.ready &&
                        !bulkListTotal.error)
                }
                retry={stepBulk === 1 && bulkListTotal.error}
            />
        </Dialog>
    );
};

interface IBulkListLoader {
    listBulk: IListBulk[];
    setListBulk: React.Dispatch<React.SetStateAction<IListBulk[]>>;
    bulkActionType: BulkActionsType;
    onReady: () => void;
    onError: () => void;
    otherGenericParamsListQuery: Compleo.IObject;
    children: React.ReactNode;
}

const BulkListLoader = (props: IBulkListLoader) => {
    const {
        listBulk,
        setListBulk,
        bulkActionType,
        onReady,
        onError,
        otherGenericParamsListQuery,
        children
    } = props;
    const status = useBulkLoadList(
        listBulk,
        setListBulk,
        true,
        bulkActionType,
        otherGenericParamsListQuery
    );
    React.useEffect(() => {
        if (status.error) onError();
        if (status.ready) onReady();
    }, [status.error, status.ready]);

    return status.ready ? <>{children}</> : <Loading />;
};

interface IPropsMainContent {
    stepBulk: number;
    bulkType: IBulkActions | null;
    setBulkType: any;
    listBulk: IListBulk[];
    setListBulk: React.Dispatch<React.SetStateAction<IListBulk[]>>;
    openDialog: boolean;
    formikRef: React.MutableRefObject<any>;
    setStepBulk: (value: React.SetStateAction<number>) => void;
    otherGenericParamsListQuery?: Compleo.IObject;
    BulkActionsRemoveFromList: BulkActionsType[];
    setLoadingButtons: React.Dispatch<React.SetStateAction<boolean>>;
    fromJobViewJobId?: string;
    nextStep: () => void;
    previousStep: () => void;
    onClose: (value?: string) => void;
    onBulkSelectionStale: () => void;
}

const MainContent = (props: IPropsMainContent) => {
    const {
        stepBulk,
        bulkType,
        setBulkType,
        listBulk,
        setListBulk,
        openDialog,
        formikRef,
        setStepBulk,
        otherGenericParamsListQuery,
        BulkActionsRemoveFromList,
        setLoadingButtons,
        fromJobViewJobId,
        nextStep,
        previousStep,
        onClose,
        onBulkSelectionStale
    } = props;

    const setAction = (type: IBulkActions) => {
        setBulkType(type);
    };

    switch (stepBulk) {
        case -1:
            return <Loading />;
        case 1:
            return (
                <BulkActionsStep1
                    setAction={setAction}
                    action={bulkType}
                    BulkActionsRemoveFromList={BulkActionsRemoveFromList}
                />
            );
        case 2:
            return (
                <BulkActionsStep2
                    BulkActionsListLocal={
                        <BulkActionsList
                            listBulk={listBulk}
                            openDialog={openDialog}
                            setListBulk={setListBulk}
                        />
                    }
                />
            );
        case 3:
            switch (bulkType?.name) {
                case "applicant_addToJob":
                    return (
                        <BulkActionsStep3AddToJob
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "applicantjob_addToStage":
                case "applicantjob_disqualify":
                case "applicantjob_requalify":
                case "applicantjob_changeRejectedStage":
                    const action = getBulkChangeStageActionName(bulkType?.name);
                    if (action) {
                        return (
                            <BulkActionsStep3AddToStageOrDisqualify
                                listBulk={listBulk}
                                formikRef={formikRef}
                                setStepBulk={setStepBulk}
                                otherGenericParamsListQuery={
                                    otherGenericParamsListQuery
                                }
                                action={action}
                                setLoadingButtons={setLoadingButtons}
                            />
                        );
                    } else {
                        return null;
                    }
                case "applicant_sendEmail":
                    return (
                        <BulkActionsStep3SendEmail
                            onSelectionStale={onBulkSelectionStale}
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                            fromJobViewJobId={fromJobViewJobId}
                        />
                    );
                case "applicant_sendWhatsappTemplate":
                case "applicantjob_requestScreeningQuestionnaireWhatsapp":
                    return (
                        <BulkActionsStep3SendWhatsappTemplate
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                            fromJobViewJobId={fromJobViewJobId}
                            bulkActionType={bulkType.name}
                            fixedGlobalTemplateKey={
                                bulkType.name ===
                                "applicantjob_requestScreeningQuestionnaireWhatsapp"
                                    ? "candidate_screening_questionnaire_request"
                                    : undefined
                            }
                        />
                    );
                case "applicant_whatsappManualOptIn":
                    return (
                        <BulkActionsStep3WhatsappManualOptIn
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "applicantjob_requestEvaluation":
                    return (
                        <BulkActionsStep3RequestEvaluation
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "applicant_Delete":
                    return (
                        <BulkActionsStep3DeleteApplicant
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "job_exportToFile":
                    return (
                        <BulkActionsStep3JobExport
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "applicant_addTag":
                    return (
                        <BulkActionsStep3ApplicantAddTags
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "applicantjob_requestAssessment":
                    return (
                        <BulkActionsStep3SendAssessment
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "applicantjob_aiMatching":
                    return (
                        <BulkActionsStep3JobAIMatch
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                            otherGenericParamsListQuery={
                                otherGenericParamsListQuery
                            }
                            onSelectionStale={onBulkSelectionStale}
                        />
                    );
                case "applicant_importFromFile":
                    return (
                        <BulkActionsStep3ImportApplicants
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                            nextStep={nextStep}
                            previousStep={previousStep}
                            onClose={onClose}
                        />
                    );
                case "applicant_exportToFile":
                    return (
                        <BulkActionsStep3ApplicantExport
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                        />
                    );
                case "applicantjob_exportToFile":
                    return (
                        <BulkActionsStep3ApplicantExport
                            listBulk={listBulk}
                            formikRef={formikRef}
                            setStepBulk={setStepBulk}
                            setLoadingButtons={setLoadingButtons}
                            isJobContext={true}
                            jobId={fromJobViewJobId}
                        />
                    );
                default:
                    return null;
            }
        case 99:
            return <BulkActionsStepConfirmation bulkAction={bulkType} />;
        default:
            return null;
    }
};

interface IDialogActionsModal {
    stepBulk: number;
    nextStep: () => void;
    bulkType: IBulkActions | null;
    previousStep: () => void;
    onClose: (value?: string) => void;
    t: any;
    loadingButtons: boolean;
    nextDisabled?: boolean;
    retry?: boolean;
}
export const DialogActionsModal = (props: IDialogActionsModal) => {
    const {
        stepBulk,
        nextStep,
        previousStep,
        onClose,
        t,
        loadingButtons,
        nextDisabled = false,
        retry = false,
        bulkType
    } = props;

    // Exceptions
    if (stepBulk === 3 && bulkType?.name === "applicant_importFromFile") {
        return null;
    }

    if (stepBulk === -1) {
        return (
            <DialogActions>
                <Loading size={20} />
            </DialogActions>
        );
    }
    switch (stepBulk) {
        case 99:
            // Confirmation
            return (
                <DialogActions>
                    <Button
                        onClick={() => onClose()}
                        color="secondary"
                        disabled={loadingButtons}
                    >
                        {t("ELASTICLISTSETTINGS:GeneralModalClose")}
                    </Button>
                </DialogActions>
            );
        default:
            return (
                <DialogActions>
                    <Button
                        onClick={() => onClose()}
                        color="secondary"
                        disabled={loadingButtons}
                    >
                        {t("ELASTICLISTSETTINGS:GeneralModalCancel")}
                    </Button>
                    {stepBulk > 1 && (
                        <Button
                            onClick={previousStep}
                            color="primary"
                            disabled={loadingButtons}
                        >
                            {t(
                                "ELASTICLISTSETTINGS:ModalBulkActions_PreviousButton"
                            )}
                        </Button>
                    )}
                    <Button
                        onClick={nextStep}
                        color="primary"
                        variant="contained"
                        disabled={loadingButtons || nextDisabled}
                        startIcon={retry ? <RefreshIcon /> : undefined}
                    >
                        {stepBulk === 3
                            ? t(
                                  "ELASTICLISTSETTINGS:ModalBulkActions_FinishButton"
                              )
                            : t(
                                  "ELASTICLISTSETTINGS:ModalBulkActions_NextButton"
                              )}
                    </Button>
                </DialogActions>
            );
    }
};

const BulkActionsModal = (props: IPropsItemFilter) => {
    if (!shouldRenderBulkActionsModal(props.open)) return null;

    return <BulkActionsModalContent {...props} />;
};

export default BulkActionsModal;
